import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { FormatUtils, useWindowSize } from '../../utils';
import styled from 'styled-components';
import { BoxV2, ColV2, EmptyStateV2, RowV2, TextV2 } from '@cubeyou-team/hx-react-components';
import uniqid from 'uniqid';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { TooltipData } from '../../components/ChartTooltip/ChartTooltip';
import ChartTooltip from '../../components/ChartTooltip';
import MosaicTooltipContent from './components/MosaicTooltipContent';
import MosaicContent from './components/MosaicContent';

type StyledMultipleMosaicChartProps = {
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  isScrollable?: boolean;
};

const StyledMultipleMosaicChartWrapper = styled.div<
  StyledMultipleMosaicChartProps
>`
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #ffffff;
  overflow: ${(props) => (props.isScrollable ? 'hidden auto' : 'hidden')};
  z-index: 0;
`;

const StyledMultipleMosaicChartSvg = styled.svg`
  z-index: 1;
`;

let tooltipTimeout: number;
type MultipleMosaicChartProps = {
  margin: any;
  data: any[];
  metric: string;
  color: string;
  emptyDataMessage: string;
  isScrollable: boolean;
  colors: any;
  sortTarget: string;
  skip: number;
  labels: any;
};

const defaultMargin = { top: 0, right: 0, bottom: 0, left: 0 };
const MultipleMosaicChart: FC<MultipleMosaicChartProps> = ({
  data,
  margin = defaultMargin,
  metric,
  emptyDataMessage,
  colors,
  sortTarget,
  skip,
  labels
}) => {
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    hideTooltip,
    showTooltip
  } = useTooltip<TooltipData>();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const windowSize = useWindowSize();
  const svgRef = useRef(null);
  const isEmpty = !data || data.length === 0;

  const chartRef = useCallback(
    (node) => {
      if (node != null) {
        setWidth(node.getBoundingClientRect().width);
        setHeight(node.getBoundingClientRect().height);
      }
    },
    [windowSize]
  );
  const targets = (!isEmpty && Object.keys(data[0].metrics)) || [];
  const chartId = useRef(uniqid());
  const sort = sortTarget || 'similar';

  const getTooltipContent = useCallback(
    (item) => {
      return targets.map((key) => {
        return {
          id: item.id,
          color: colors[key],
          ...item.metrics[key]
        };
      });
    },
    [targets, colors]
  );

  const mosaicChartData: any = useMemo(() => {
    const keys = sort !== 'similar' ? targets : ['similar'];
    const chartData = keys.reduce((acc, targetId, targetIndex) => {
      const list = data.reduce((list, d, index) => {
        if (
          sort !== 'different' ||
          (sort === 'different' &&
            (1 * skip + index) % keys.length === targetIndex)
        ) {
          let label = d.label;
          if (!['different', 'similar'].includes(sort) && sort !== targetId) {
            const mainValue = (d.metrics[sort] && d.metrics[sort][metric]) || 0;
            const value = d.metrics[targetId][metric] || 0;
            const diff = Math.abs(mainValue - value);
            const sign = value && value > mainValue ? '+' : '-';

            label =
              diff > 0
                ? sign + FormatUtils.formattedValue(diff, metric)
                : FormatUtils.formattedValue(diff, metric);
          }

          let bars = [];
          if (d && d.metrics) {
            const sum = [
              ...targets.map((targetId) => d.metrics[targetId][metric])
            ].reduce((total, value) => total + value, 0);
            bars = targets.map((target) => {
              return {
                color: colors[target],
                width: sum > 0 ? (d.metrics[target][metric] / sum) * 100 : 0
              };
            });
          }

          const item = {
            id: d.id,
            target: targetId,
            insight_type: d.insight_type,
            label,
            description: d.label,
            pic: d.pic,
            bars,
            tooltip: getTooltipContent(d),
            ...d.metrics[targetId]
          };
          list.push(item);
        }
        return list;
      }, []);
      acc[targetId] = list;
      return acc;
    }, {});

    return chartData;
  }, [data, sort, metric, skip, colors, targets]);

  const handleMouseLeave = useCallback(
    (event) => {
      tooltipTimeout = window.setTimeout(() => {
        hideTooltip();
      }, 0);
    },
    [hideTooltip]
  );

  const handleMouseMove = useCallback(
    (event, item) => {
      const coords = localPoint(svgRef.current, event);
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      showTooltip({
        tooltipData: {
          ...item,
          label: item.description
        },
        tooltipTop: coords?.y,
        tooltipLeft: coords?.x
      });
    },
    [showTooltip, svgRef.current]
  );

  if (isEmpty) {
    return (
      <StyledMultipleMosaicChartWrapper>
        <BoxV2 minWidth='100%'>
          <EmptyStateV2
            icon='analytics'
            mainText={emptyDataMessage}
            size='large'
          />
        </BoxV2>
      </StyledMultipleMosaicChartWrapper>
    );
  }
  return (
    <StyledMultipleMosaicChartWrapper ref={chartRef}>
      <StyledMultipleMosaicChartSvg ref={svgRef} width={width} height={height}>
        <foreignObject x='0' y='0' width={width} height={height}>
          {sort !== 'similar' ? (
            <RowV2
              valign='equal'
              halign='left'
              style={{ height: '100%', width: '100%' }}
            >
              {targets.map((key, index) => {
                return (
                  <ColV2
                    span={sort !== 'similar' ? 12 / targets.length : 12}
                    key={`col-${chartId.current}-${index}`}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          height: '80px',
                          maxHeight: '80px',
                          minHeight: '80px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <TextV2
                          variant='subtitle-2'
                          lettercase='uppercase'
                          align='center'
                          style={{
                            color: colors[key],
                            maxWidth: '100%',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden'
                          }}
                        >
                          {labels[key]}
                        </TextV2>
                      </div>
                      <MosaicContent
                        margin={margin}
                        data={mosaicChartData[key]}
                        color={colors[key]}
                        sort={sort}
                        chartId={chartId.current}
                        showItemsPreview={sort === 'different' || sort === key}
                        handleMouseMove={handleMouseMove}
                        handleMouseLeave={handleMouseLeave}
                      />
                    </div>
                  </ColV2>
                );
              })}
            </RowV2>
          ) : (
            <BoxV2 style={{ height: '100%', width: '100%' }}>
              <MosaicContent
                width={width}
                height={height}
                margin={margin}
                data={mosaicChartData.similar}
                colors='#d6d6d6'
                sort={sort}
                chartId={chartId.current}
                showItemsPreview
                handleMouseMove={handleMouseMove}
                handleMouseLeave={handleMouseLeave}
              />
            </BoxV2>
          )}
        </foreignObject>
      </StyledMultipleMosaicChartSvg>
      {tooltipOpen && tooltipData && (
        <ChartTooltip
          opened={tooltipOpen}
          top={tooltipTop || 0}
          left={tooltipLeft || 0}
          data={tooltipData}
        >
          <MosaicTooltipContent data={tooltipData} chartId={chartId.current} />
        </ChartTooltip>
      )}
    </StyledMultipleMosaicChartWrapper>
  );
};

export default MultipleMosaicChart;
